import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AreaChart, ColumnChart, MAIN_CHART_HEIGHT } from '@/features/dashboard-charts';
import { getMetricLabel, getWarehouseChartTitle } from '@/features/dashboard-filters';
import { getExportOptions } from '@/features/chart-export';

import { PageHeader } from '@/shared/ui';
import { MANUAL_NODES } from '@/shared/config/manual';

import type { useStorageOverview } from '../lib/use-storage-overview';
import { toMovementsColumns } from '../lib/movements-chart';
import { ChartSection, TopListsGrid, MovementsStats, StockOnHandStats } from './components';

interface StorageOverviewPageProps {
  /** Owned by `StorageOverviewWithFilters`, which also publishes it to the header. */
  storage: ReturnType<typeof useStorageOverview>;
}

export function StorageOverviewPage({ storage }: StorageOverviewPageProps) {
  const { t } = useTranslation();
  const { data, formattedStats, isLoading, isFetching, error, urlState, updateOption } = storage;

  const movementsExportOptions = useMemo(() => getExportOptions('warehouse-movements'), []);
  const sohExportOptions = useMemo(() => getExportOptions('warehouse-stock-on-hand'), []);
  const movementsChart = useMemo(
    () =>
      data?.warehouseMovements && {
        ...toMovementsColumns(data.warehouseMovements, urlState.group),
        title: getWarehouseChartTitle(t, 'movements', urlState.group),
        metricLabel: getMetricLabel(t, urlState.metric)
      },
    [data?.warehouseMovements, t, urlState.group, urlState.metric]
  );
  const sohChart = useMemo(
    () =>
      data?.warehouseSOH && {
        ...data.warehouseSOH,
        weekly: urlState.group === 'W',
        title: getWarehouseChartTitle(t, 'soh', urlState.group),
        metricLabel: getMetricLabel(t, urlState.metric)
      },
    [data?.warehouseSOH, t, urlState.group, urlState.metric]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      updateOption('tab', value);
    },
    [updateOption]
  );

  if (error) {
    return (
      <div>
        <Card className="p-4 md:p-6">
          <p className="text-destructive">Error loading storage overview data</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader
        title={t('report.storageOverview.title', 'Storage Overview')}
        helpTarget={MANUAL_NODES.STORAGE_OVERVIEW}
      />

      <Tabs value={urlState.tab} onValueChange={handleTabChange} className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <TabsList className="grid grid-cols-2 w-full md:w-max">
            <TabsTrigger className="px-12" value="soh">
              {t('welcome.chart.type.soh.label', 'Stock on Hand')}
            </TabsTrigger>
            <TabsTrigger className="px-12" value="movements">
              {t('welcome.chart.type.smv.label', 'Stock Movements')}
            </TabsTrigger>
          </TabsList>
          <div className="w-full min-w-0 2xl:flex 2xl:w-auto 2xl:flex-1 2xl:justify-end">
            <div className="2xl:w-2/3">
              {urlState.tab === 'movements' ? (
                <MovementsStats stats={formattedStats} isLoading={isLoading || isFetching} />
              ) : (
                <StockOnHandStats stats={formattedStats} isLoading={isLoading || isFetching} />
              )}
            </div>
          </div>
        </div>

        <TabsContent value="movements" className="space-y-6">
          <ChartSection>
            <ColumnChart
              data={movementsChart}
              height={MAIN_CHART_HEIGHT}
              loading={isLoading || isFetching}
              showExport
              exportOptions={movementsExportOptions}
            />
          </ChartSection>
          <TopListsGrid
            topWarehouses={data?.topWarehouses}
            topUsers={data?.topUsers}
            isLoading={isLoading || isFetching}
          />
        </TabsContent>

        <TabsContent value="soh" className="space-y-6">
          <ChartSection>
            <AreaChart
              data={sohChart}
              height={MAIN_CHART_HEIGHT}
              loading={isLoading || isFetching}
              showExport
              exportOptions={sohExportOptions}
            />
          </ChartSection>
          <TopListsGrid
            topWarehouses={data?.topWarehouses}
            topUsers={data?.topUsers}
            isLoading={isLoading || isFetching}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
